import { defineStore } from 'pinia';
import { notify } from '@kyvg/vue3-notification';

export const useCustomerAutomationsStore = defineStore('customerAutomations', () => {
    const automation = ref();

    const eventTypes = {
        IDLE_IN_PIPE: 'Idle in pipe',
        UNRESPONSIVE: 'Unresponsive',
        FORM_COMPLETED: 'Form completed',
        JOB_CONFIRMED: 'Job confirmed',
        PROSPECT_INVITATION_ACCEPTANCE: 'Prospect invitation acceptance (still in "New" state)',
        PROSPECT_INVITATION_PRE_ACCEPTANCE: 'Prospect invitation pre acceptance',
    };

    const automationTypes = {
        EMAIL: 'Email',
        SMS: 'SMS',
    };

    function buildAutomationPayload(values) {
        const payload = { ...values };

        // Normalise order tags + filter type according to backend contract
        const rawTags = values?.order_tags ?? [];
        const normalisedTags =
            Array.isArray(rawTags) ?
                rawTags
                    .map((tag) => {
                        if (typeof tag === 'string') return tag;
                        if (tag?.id) return tag.id;
                        if (tag?.value) return tag.value;
                        if (tag?.name) return tag.name;
                        return null;
                    })
                    .filter((tag) => !!tag)
            :   [];

        if (!normalisedTags.length) {
            // No tag filter applied – omit both fields
            delete payload.order_tags;
            delete payload.order_tag_filter_type;
        } else {
            payload.order_tags = normalisedTags;
            // Default to "ANY" if tags selected but no filter type chosen
            payload.order_tag_filter_type = values?.order_tag_filter_type || 'ANY';
        }

        return payload;
    }

    async function createAutomation(values) {
        const payload = buildAutomationPayload(values);
        await window.axios.post('customer-automations', payload);
    }

    async function retrieveAutomation(id) {
        const response = await window.axios.get('customer-automations/' + id);
        automation.value = formatData(response.data.customer_automation);
    }

    async function updateAutomation(id, values) {
        const payload = buildAutomationPayload(values);
        await window.axios.patch('customer-automations/' + id, payload);
    }

    async function createStep(automation_id, values) {
        await window.axios.post('customer-automations/' + automation_id + '/steps', values);
    }

    async function updateStep(automation_id, step_id, values) {
        await window.axios.patch('customer-automations/' + automation_id + '/steps/' + step_id, values);
    }

    async function deleteStep(step_id) {
        const response = await window.axios.delete('customer-automations/' + automation.value.id + '/steps/' + step_id);
        await retrieveAutomation(automation.value.id);
        notify({
            title: 'Undo delete',
            group: 'app_restore',
            text: 'delete',
            data: {
                url: `customer-automations/${automation.value.id}/steps`,
                type: 'Step',
                lastDeleted: { id: step_id },
                key: 'customer-automations',
            },
        });
        return response;
    }

    async function restoreStep(step_id) {
        step_id;
    }

    function formatData(data) {
        let {
            id,
            label,
            description,
            active,
            end_on_engagement,
            event,
            trigger_start_date,
            flow_end_date,
            order_tags,
            order_tag_filter_type,
            steps,
        } = data;

        steps = steps.map(({ id, trigger_delay, automation_type, automation_template_id, active }) => {
            return {
                id,
                trigger_delay,
                automation_type,
                automation_template_id,
                active,
            };
        });

        return {
            id,
            label,
            description,
            active,
            end_on_engagement,
            event,
            trigger_start_date,
            flow_end_date,
            // Normalise for the form: always an array
            order_tags: order_tags ?? [],
            order_tag_filter_type,
            steps,
        };
    }

    return {
        automation,
        createAutomation,
        retrieveAutomation,
        updateAutomation,
        createStep,
        updateStep,
        deleteStep,
        restoreStep,
        eventTypes,
        automationTypes,
    };
});
